// ClipReviewSandbox.jsx
// Controlled test surface for the Chrome-extension clip-review bot, rebuilt
// to match the REAL Whop Content Rewards review screen (confirmed from
// screenshots): title, submitter avatar+username+time, est. payout, a
// status pill, a platform icon, a "Watch video" link-out (Whop shows no
// inline preview), and Views/Likes/Comments/Shares stats + a growth curve.
// Flag/Approve/Reject buttons sit top-right, matching the real layout.
//
// Important: Whop's real card does NOT show bio, profile picture, or a
// full caption with hashtags -- that data only exists on the actual post.
// In production, apify_check.py fetches it from there. For fast/free
// sandbox testing of the DECISION logic (not the scraping mechanics,
// which apify_check.py already covers separately), that same data is
// embedded here as hidden fields the extension reads directly and sends
// with verify_external=false.
import { useEffect, useMemo, useState } from "react";
import "./ClipReviewSandbox.css";

// Real brief, copied verbatim from the campaign's Google Doc (2026-08).
// Replaces the earlier hand-typed brief, which got two things backwards:
// it said usernames must NOT reference James O'Keefe (actual rule: they
// SHOULD, e.g. "okeefe.clips" -- just can't pose as his official account)
// and that the pfp must be "a real photo" (actual rule: a clean clip/
// brand-style image is fine, doesn't need to be a photo at all).
const BRIEF = `James O'Keefe Clipping -- Full Guide
You need a dedicated page for this campaign OR 5k+ followers on a theme page in the political space. Platforms: TikTok, Instagram, YouTube only.

Who you're clipping: James O'Keefe, founder of O'Keefe Media Group (OMG).

Page setup rules:
- Post on a dedicated James O'Keefe clip page -- obvious in one second what it is.
- Do not run this campaign on the same page as another campaign.
- Bio must clearly state this is a fan/clip page, not James O'Keefe himself. Use this line exactly: "Fan clip page – not affiliated with or impersonating James O'Keefe."
- Bio must include this exact link, unchanged: https://okeefemediagroup.com/get-full-access/
- Bio line 2 should be: "Subscribe & Support the Fight 👇"
- Profile picture: a clean clip/brand-style image (logo, graphic, etc. is fine) -- just don't pose as his official account.
- Page name: "James O'Keefe Clips" or a clean variation.
- Username: SHOULD be tied to him (e.g. okeefe.clips, jamesokeefe.moments) -- a fan-page-style handle is the goal, not something unrelated. It must not claim to BE his official account.
- No bots: minimum 10% engagement rate (likes+comments / views). Real organic comments can still get approved under 10%; fake views/comments are auto-banned.

Every clip needs:
- James clearly speaking (no pure B-roll).
- One idea per clip, one hook.
- Clean modern editing, subtitles required, white/black/gold text only, kept out of the UI button zones.
- Tag @JamesOKeefeIII in the caption (TikTok/Instagram) or @OKeefeMediaGroup in the title (YouTube).
- Caption must direct viewers to the link in bio (CTA).
- Drop the subscription link in the comments and pin it.
- Do not follow James's main pages from the clip page.
- Minimum 15 seconds long.
- No AI edits or AI subtitles (Clipbait.ai is the only allowed exception).
- No aura/glow/skull edits. No lazy reposts -- must be a properly edited, unique clip.
- No personal opinions inserted as James's -- clip his actual words, don't editorialize on top.
- Only use James's official source content (provided source links), not outside footage.
- Minimum 40% Tier-1 audience (US/UK/CA/AU).

Pay: $1.50 CPM, min payout $3, max payout $300, total budget $1000.`;

const CAMPAIGN_HEADER = "James O'Keefe Clipping | $3500 Budget | $1.50 CPM";
const CAMPAIGN_RATE = "$1.50 / 1k views";

// PNG via canvas, not an SVG data URI -- Gemini's API rejects
// image/svg+xml with "Unsupported MIME type" (confirmed by a real call),
// so any placeholder image sent for vision compliance checking has to be
// an actual raster format.
function avatarDataUri(seed, color) {
  const canvas = document.createElement("canvas");
  canvas.width = 60;
  canvas.height = 60;
  const ctx = canvas.getContext("2d");
  // A flat solid-color circle reads as "not a designed image" to a vision
  // model -- a white ring + inner accent bar is closer to a plausible
  // simple logo treatment, which is what the pfp check is actually meant
  // to allow ("a clean clip/brand-style image", not necessarily a photo).
  ctx.fillStyle = "#111";
  ctx.beginPath();
  ctx.arc(30, 30, 30, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(30, 30, 25, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(30, 30, 25, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.font = "bold 24px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText((seed[0] || "?").toUpperCase(), 30, 31);
  return canvas.toDataURL("image/png");
}
// A simple rising sparkline -- visual flavor only, matching the growth
// curve Whop's real stats panel shows. `steepness` just bends the curve.
function Sparkline({ steepness = 1, color = "#e879f9" }) {
  const points = Array.from({ length: 10 }, (_, i) => {
    const x = (i / 9) * 100;
    const y = 100 - Math.pow(i / 9, 1 / steepness) * 90;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="crs-sparkline">
      <polyline points={points} fill="none" stroke={color} strokeWidth="3" />
    </svg>
  );
}

// Ground truth is intentionally varied: clean/compliant, each individual
// brief violation in isolation, several bot-engagement patterns, a
// duplicate-URL case, and a "small but legit" case to check for false
// positives on new/small accounts. `external*` fields represent what
// apify_check.py would fetch from the real post in production.
const CLEAN_BIO = "Fan clip page – not affiliated with or impersonating James O'Keefe. Subscribe & Support the Fight 👇 https://okeefemediagroup.com/get-full-access/";
const CLEAN_CAPTION = "Comment SUBSCRIBE for full access to the story they don't want you to see. Tag @JamesOKeefeIII – link in bio.";
// Brief requires two DIFFERENT things: a casual, okeefe-tied handle (fine
// to vary, e.g. "okeefe.clips") and a specific page/display name ("James
// O'Keefe Clips" or a clean variation) -- conflating them into one value
// was a real test-data bug that made a compliant case (sub_09) fail on a
// username_ok check that had nothing to do with what it was testing.
const CLEAN_PAGE_NAME = "James O'Keefe Clips";

const SUBMISSIONS = [
  {
    id: "sub_01", expected: "approve", platform: "TikTok",
    title: "James O'Keefe exposes it live", submitter: "okeefe.clips", avatarColor: "#e1306c",
    timeAgo: "2 hours ago", estPayout: 61.80, views: 41200, likes: 3500, comments: 800, shares: 310,
    watchUrl: "https://tiktok.com/@okeefe.clips/video/1", steepness: 1.4,
    externalUsername: "@okeefe.clips", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: CLEAN_CAPTION, externalPfp: avatarDataUri("O", "#e1306c"),
  },
  {
    id: "sub_02", expected: "reject_brief", platform: "Instagram",
    title: "crazy clip you have to see", submitter: "clipking99", avatarColor: "#405de6",
    timeAgo: "5 hours ago", estPayout: 22.95, views: 15300, likes: 1800, comments: 300, shares: 44,
    watchUrl: "https://instagram.com/p/clipking99_1", steepness: 1.2,
    externalUsername: "@clipking99", externalBio: "just a guy who clips stuff",
    externalCaption: "wow crazy video, check it out", externalPfp: avatarDataUri("C", "#405de6"),
    note: "No @JamesOKeefeIII tag, no bio-link CTA in caption, bio missing the required disclaimer/link entirely.",
  },
  {
    id: "sub_03", expected: "reject_brief", platform: "X",
    title: "watch this clip of okeefe", submitter: "okeefe.moments", avatarColor: "#000",
    timeAgo: "12 hours ago", estPayout: 14.70, views: 9800, likes: 1200, comments: 200, shares: 61,
    watchUrl: "https://x.com/okeefe_moments/status/1", steepness: 1.1,
    externalUsername: "@okeefe.moments", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: CLEAN_CAPTION, externalPfp: avatarDataUri("M", "#000"),
    note: "Submitted via X -- brief only allows TikTok, Instagram, YouTube. Everything else here is compliant.",
  },
  {
    id: "sub_04", expected: "reject_brief", platform: "YouTube",
    title: "Full breakdown of the confrontation", submitter: "jamesokeefe.moments", avatarColor: "#ff0000",
    timeAgo: "1 day ago", estPayout: 33.00, views: 22000, likes: 2500, comments: 400, shares: 88,
    watchUrl: "https://youtube.com/shorts/jamesokeefe_moments_1", steepness: 1.3,
    externalUsername: "@jamesokeefe.moments", externalProfileName: CLEAN_PAGE_NAME, externalBio: "Fan clip page – not affiliated with or impersonating James O'Keefe.",
    externalCaption: "Full breakdown – tag @OKeefeMediaGroup", externalPfp: avatarDataUri("J", "#ff0000"),
    note: "Bio has the disclaimer line but is missing the required link to okeefemediagroup.com/get-full-access/.",
  },
  {
    id: "sub_05", expected: "reject_bot", platform: "TikTok",
    title: "insane moment caught on camera", submitter: "okclipper_viral", avatarColor: "#25f4ee",
    timeAgo: "3 hours ago", estPayout: 280.50, views: 187000, likes: 0, comments: 0, shares: 0,
    watchUrl: "https://tiktok.com/@okclipper_viral/video/1", steepness: 4,
    externalUsername: "@okeefe.viral", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: CLEAN_CAPTION, externalPfp: avatarDataUri("V", "#25f4ee"),
    note: "187K views, ZERO likes/comments (0% engagement, brief requires 10%) -- classic bot pattern. Text compliance is otherwise clean, isolating the bot signal.",
  },
  {
    id: "sub_06", expected: "reject_bot", platform: "Instagram",
    title: "wow can't believe this happened", submitter: "okeefe.fastclips", avatarColor: "#f09433",
    timeAgo: "30 minutes ago", estPayout: 93.00, views: 62000, likes: 4000, comments: 900, shares: 20,
    watchUrl: "https://instagram.com/p/okeefe_fastclips_1", steepness: 3.5,
    externalUsername: "@okeefe.fastclips", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: CLEAN_CAPTION, externalPfp: avatarDataUri("F", "#f09433"),
    note: "62K views in 30 minutes on a small account -- view-velocity spike, even though engagement rate and text compliance look fine.",
  },
  {
    id: "sub_07", expected: "reject_bot", platform: "TikTok",
    title: "James O'Keefe exposes it live", submitter: "okeefe.sam", avatarColor: "#000",
    timeAgo: "8 hours ago", estPayout: 7.50, views: 5000, likes: 640, comments: 210, shares: 12,
    watchUrl: "https://tiktok.com/@okeefe.clips/video/1", steepness: 1.1,
    externalUsername: "@okeefe.sam", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: CLEAN_CAPTION, externalPfp: avatarDataUri("S", "#000"),
    note: "Same watch URL as sub_01 -- duplicate/reused submission from a different account.",
  },
  {
    id: "sub_08", expected: "flag", platform: "YouTube",
    title: "worth watching this one", submitter: "okeefe.maybe", avatarColor: "#ff0000",
    timeAgo: "15 hours ago", estPayout: 12.45, views: 8300, likes: 700, comments: 130, shares: 9,
    watchUrl: "https://youtube.com/shorts/okeefe_maybe_1", steepness: 1.2,
    externalUsername: "@okeefe.maybe", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: "worth watching this one, subscribe for more", externalPfp: avatarDataUri("M", "#ff0000"),
    note: "No @OKeefeMediaGroup tag in the title and no explicit bio-link CTA, but engagement (~10%) looks organic and everything else is clean -- borderline.",
  },
  {
    id: "sub_09", expected: "approve", platform: "Instagram",
    title: "posting my first clip!", submitter: "okeefe.newbie", avatarColor: "#833ab4",
    timeAgo: "2 days ago", estPayout: 0.96, views: 640, likes: 50, comments: 15, shares: 2,
    watchUrl: "https://instagram.com/p/okeefe_newbie_1", steepness: 1.1,
    externalUsername: "@okeefe.newbie", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: CLEAN_CAPTION, externalPfp: avatarDataUri("N", "#833ab4"),
    note: "Small new account, modest views, ~10.2% engagement -- should NOT be flagged just for being small.",
  },
  {
    id: "sub_10", expected: "reject_bot", platform: "TikTok",
    title: "unreal footage from today", submitter: "okeefe.bulk", avatarColor: "#fe2c55",
    timeAgo: "3 days ago", estPayout: 510.00, views: 340000, likes: 210, comments: 3, shares: 5,
    watchUrl: "https://tiktok.com/@okeefe.bulk/video/1", steepness: 2.8,
    externalUsername: "@okeefe.bulk", externalProfileName: CLEAN_PAGE_NAME, externalBio: CLEAN_BIO,
    externalCaption: CLEAN_CAPTION, externalPfp: avatarDataUri("B", "#fe2c55"),
    note: "340K views but 0.06% engagement (brief requires 10%) -- views far exceed plausible organic reach.",
  },
];

export default function ClipReviewSandbox() {
  const [verdicts, setVerdicts] = useState({}); // id -> { verdict, at }
  const submissions = useMemo(() => SUBMISSIONS, []);

  useEffect(() => {
    window.__clipSandbox = { brief: BRIEF, submissions };
    return () => { delete window.__clipSandbox; };
  }, [submissions]);

  const record = (id, verdict) => {
    setVerdicts((prev) => ({ ...prev, [id]: { verdict, at: Date.now() } }));
  };

  const graded = Object.entries(verdicts);
  const correct = graded.filter(([id, v]) => matchesExpected(id, v.verdict, submissions)).length;

  return (
    <div className="crs-page">
      <div className="crs-header">
        <h1>Clip Review Sandbox</h1>
        <p className="crs-sub">Dummy submissions only, laid out to match the real Whop Content Rewards review screen -- nothing here is a real campaign or payout.</p>
        {graded.length > 0 && (
          <div className="crs-score">Scored {correct}/{graded.length} correct ({Math.round((correct / graded.length) * 100)}%)</div>
        )}
      </div>

      <details className="crs-brief">
        <summary>Campaign brief (what the bot should be checking against)</summary>
        <pre>{BRIEF}</pre>
      </details>

      <div className="crs-list">
        {submissions.map((s) => {
          const v = verdicts[s.id];
          const isCorrect = v && matchesExpected(s.id, v.verdict, submissions);
          const status = v ? v.verdict : "pending";
          return (
            <div key={s.id} className="crs-card" data-submission-id={s.id} data-expected={s.expected}>
              <div className="crs-card-header">
                <div>
                  <div className="crs-campaign-name">{CAMPAIGN_HEADER}</div>
                  <div className="crs-campaign-rate">{CAMPAIGN_RATE}</div>
                </div>
                <div className="crs-actions">
                  <button className="crs-icon-btn" data-action="flag" onClick={() => record(s.id, "flag")} title="Flag">⚑</button>
                  <button className="crs-btn approve" data-action="approve" onClick={() => record(s.id, "approve")}>Approve</button>
                  <button className="crs-btn reject" data-action="reject" onClick={() => record(s.id, "reject")}>Reject</button>
                </div>
              </div>

              <div className="crs-body">
                <div className="crs-main">
                  <div className="crs-title" data-role="title">{s.title}</div>
                  <div className="crs-submitted-by">
                    <img src={avatarDataUri(s.submitter, s.avatarColor)} alt="" className="crs-avatar" />
                    <span>{s.timeAgo} by <b data-role="submitter">{s.submitter}</b></span>
                  </div>

                  <div className="crs-meta-row">
                    <span className="crs-payout">Est. payout <b>${s.estPayout.toFixed(2)}</b></span>
                    <span className="crs-shield" title="Whop's own trust/safety indicator (meaning unconfirmed)">🛡</span>
                    <span className={`crs-status-pill ${status}`}>{status}</span>
                  </div>

                  <div className="crs-platform-row">
                    <span className="crs-platform-icon">{s.platform}</span>
                    <a href={s.watchUrl} target="_blank" rel="noreferrer" className="crs-watch-btn" data-role="watch-url">Watch video ↗</a>
                  </div>
                </div>

                <div className="crs-stats-panel" data-role="stats"
                     data-views={s.views} data-likes={s.likes} data-comments={s.comments} data-shares={s.shares} data-url={s.watchUrl}>
                  <div className="crs-stats-numbers">
                    <div><span>Views</span><b>{s.views.toLocaleString()}</b></div>
                    <div><span>Likes</span><b>{s.likes.toLocaleString()}</b></div>
                    <div><span>Comments</span><b>{s.comments.toLocaleString()}</b></div>
                    <div><span>Shares</span><b>{s.shares.toLocaleString()}</b></div>
                  </div>
                  <Sparkline steepness={s.steepness} />
                </div>
              </div>

              {/* Hidden -- represents what apify_check.py fetches from the real post in
                  production (Whop's real UI shows none of this). Used for fast/free
                  sandbox testing of decision logic with verify_external=false. */}
              <div className="crs-external-data" data-role="external"
                   data-username={s.externalUsername} data-profile-name={s.externalProfileName || s.externalUsername?.replace("@", "")}
                   data-bio={s.externalBio} data-caption={s.externalCaption}
                   data-pfp={s.externalPfp} hidden />

              {s.note && <div className="crs-note">{s.note}</div>}
              {v && (
                <div className={`crs-result ${isCorrect ? "correct" : "wrong"}`}>
                  Bot said <b>{v.verdict}</b> -- {isCorrect ? "correct" : `expected ${s.expected.replace("reject_brief", "reject").replace("reject_bot", "reject")}`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function matchesExpected(id, verdict, submissions) {
  const s = submissions.find((x) => x.id === id);
  if (!s) return false;
  const expected = s.expected.startsWith("reject") ? "reject" : s.expected; // reject_brief/reject_bot both count as "reject"
  return verdict === expected;
}
